import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { palettes, sectionRamps } from "./palette";

export type PaletteName = keyof typeof palettes;
export interface SitePalette {
  navbar: string;
  hero: string;
  featured: string;
  story: string;
  cta: string;
  footer: string;

  text: string;
  paper: string;
  primary: string;
  secondary: string;

  controlBg: string;
  controlBorder: string;
  controlText: string;
  controlHoverBg: string;

  navbarText: string;
}

interface ThemeContextValue {
  paletteName: PaletteName;
  palette: SitePalette;
  setPaletteName: (name: PaletteName) => void;
  startIndex: number;
  setStartIndex: (index: number) => void;
  maximumStart: number;
}
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface SiteThemeProviderProps {
  children: ReactNode;
}

export function SiteThemeProvider({ children }: SiteThemeProviderProps) {
  const [paletteName, setPaletteName] = useState<PaletteName>("sand");

  const [startIndex, setStartIndex] = useState(0); //0

  const ramp = sectionRamps[paletteName];

  const maximumStart = Math.max(0, ramp.length - 5);

  const safeStartIndex = Math.min(Math.max(startIndex, 0), maximumStart);

  const sectionColours = {
    navbar: ramp[safeStartIndex],
    hero: ramp[safeStartIndex],
    featured: ramp[safeStartIndex + 1],
    story: ramp[safeStartIndex + 2],
    cta: ramp[safeStartIndex + 3],
    footer: ramp[safeStartIndex + 4],
  };

  const value = useMemo(
    () => ({
      paletteName,

      palette: {
        ...palettes[paletteName],
        ...sectionColours,
      },

      setPaletteName,
      startIndex,
      setStartIndex,
      maximumStart,
    }),
    [
      paletteName,
      startIndex,
      maximumStart,
      sectionColours.navbar,
      sectionColours.hero,
      sectionColours.featured,
      sectionColours.story,
      sectionColours.cta,
      sectionColours.footer,
    ],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useSiteTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useSiteTheme must be used within SiteThemeProvider");
  }

  return context;
}
